import UploadFileIcon from '@mui/icons-material/UploadFile';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useId, useRef, useState, type DragEvent } from 'react';

import { Button } from '@/components/ui/Button';
import { LiveRegion } from '@/components/ui/LiveRegion';
import { sizeInWords, typeWord } from '@/lib/files';

interface FileDropZoneProps {
  /** What the file is, in words: "Document file". Names the group. */
  label: string;
  /** The accepted types and size, in words, under the control. */
  hint: string;
  /** The `accept` attribute: the accepted media types, comma-separated. */
  accept: string;
  /** The file chosen so far, or null. The caller holds it. */
  value: File | null;
  /**
   * A file was chosen or dropped. The caller judges it and either keeps it
   * as `value` or hands back an `error`; the zone keeps nothing itself.
   */
  onChange: (file: File) => void;
  onRemove: () => void;
  /** Why the last file was refused, linked to the group and announced. */
  error?: string | null;
  disabled?: boolean;
  /** Said beside a disabled Choose, so a person knows it will come back. */
  disabledReason?: string;
  /** True while the file is being sent: the bar shows and the controls wait. */
  busy?: boolean;
  /** Announced while busy: "Uploading guide.pdf". */
  busyMessage?: string;
  /**
   * Names the group for the testing team's tests. The hidden file input is
   * `<testId>-file-input`, which Playwright's `setInputFiles()` accepts. See
   * docs/test-ids.md.
   */
  testId: string;
}

/**
 * One file: a drop target that is also a plain button, the file chosen so
 * far, and the reason the last one was refused.
 *
 * Dropping is additive. The keyboard route is the Choose button and the
 * hidden input, which is all WCAG asks for; the highlighted border is for the
 * pointer. Unlike `ImageUpload`, nothing is sent on choice and nothing is
 * previewed: the file waits with the rest of a form and goes when the form
 * does, so the Upload and Replace dialogs share this and decide the rest.
 */
export function FileDropZone({
  label,
  hint,
  accept,
  value,
  onChange,
  onRemove,
  error = null,
  disabled = false,
  disabledReason,
  busy = false,
  busyMessage,
  testId,
}: FileDropZoneProps) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const errorId = useId();
  const hintId = useId();
  const inert = disabled || busy;

  function take(files: FileList | null | undefined) {
    const first = files?.[0];
    if (first) onChange(first);
  }

  function dragOver(event: DragEvent) {
    if (inert) return;
    event.preventDefault();
    setOver(true);
  }

  function drop(event: DragEvent) {
    event.preventDefault();
    setOver(false);
    if (inert) return;
    take(event.dataTransfer?.files);
  }

  const announcement = busy
    ? (busyMessage ?? 'Sending the file')
    : error
      ? error
      : value
        ? `${value.name} selected`
        : '';

  return (
    <Box
      role="group"
      aria-label={label}
      aria-describedby={[hintId, error ? errorId : null].filter(Boolean).join(' ')}
      data-testid={testId}
      onDragOver={dragOver}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          textAlign: 'center',
          borderStyle: 'dashed',
          borderColor: over ? 'primary.main' : 'divider',
          bgcolor: over ? 'action.selected' : 'action.hover',
        }}
      >
        <UploadFileIcon aria-hidden color={inert ? 'disabled' : 'action'} />
        {value ? (
          <Typography variant="body2" data-testid={`${testId}-file-value`}>
            <strong>{value.name}</strong>
            {' · '}
            {typeWord(value.type)}
            {' · '}
            {sizeInWords(value.size)}
          </Typography>
        ) : (
          <Typography variant="body2">Drag a file here, or choose one.</Typography>
        )}
        <Typography
          id={hintId}
          variant="caption"
          color="text.secondary"
          data-testid={`${testId}-hint`}
        >
          {hint}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={inert}
            onClick={() => input.current?.click()}
            data-testid={`${testId}-choose-button`}
          >
            {value ? 'Replace file' : 'Choose file'}
          </Button>
          {value ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={inert}
              onClick={onRemove}
              data-testid={`${testId}-remove-button`}
            >
              Remove file
            </Button>
          ) : null}
        </Box>
        {disabled && disabledReason ? (
          <Typography variant="caption" color="text.secondary">
            {disabledReason}
          </Typography>
        ) : null}

        <input
          ref={input}
          type="file"
          accept={accept}
          hidden
          disabled={inert}
          data-testid={`${testId}-file-input`}
          onChange={(event) => {
            take(event.target.files);
            // The same file chosen twice in a row must fire again.
            event.target.value = '';
          }}
        />
      </Paper>

      {busy ? <LinearProgress aria-hidden data-testid={`${testId}-progress`} /> : null}
      <LiveRegion message={announcement} />

      {error ? (
        <Alert severity="error" id={errorId} data-testid={`${testId}-error`}>
          {error}
        </Alert>
      ) : null}
    </Box>
  );
}
